// Work Health + Workload chips — the ONE place the health/band vocabulary is
// rendered. Product rules: describe the work, never judge the person ("High
// workload", "3 overdue work items"); the BLOCKED health reads "On hold"; no
// percentages; every chip can answer "Why?" with the server's reasons.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/features/shared/components/ui/popover';
import type { EffortSize, WorkHealth, WorkloadBand, WorkloadConfidence } from '@/services/workService';

type T = (key: string, opts?: Record<string, unknown>) => string;

export const HEALTH_ORDER: WorkHealth[] = ['OVERDUE', 'AT_RISK', 'STALE', 'BLOCKED', 'AWAITING_REVIEW', 'ON_TRACK'];

/** Label per health — BLOCKED is deliberately "On hold". */
export function healthLabel(t: T, health: WorkHealth): string {
  switch (health) {
    case 'ON_TRACK': return t('health_on_track', { defaultValue: 'On track' });
    case 'AT_RISK': return t('health_at_risk', { defaultValue: 'At risk' });
    case 'OVERDUE': return t('health_overdue', { defaultValue: 'Overdue' });
    case 'STALE': return t('health_stale', { defaultValue: 'Stale' });
    case 'BLOCKED': return t('health_on_hold', { defaultValue: 'On hold' });
    case 'AWAITING_REVIEW': return t('health_awaiting_review', { defaultValue: 'Awaiting review' });
    default: return String(health).replace(/_/g, ' ').toLowerCase();
  }
}

export const HEALTH_CLASS: Record<WorkHealth, string> = {
  ON_TRACK: 'bg-success-soft text-success-strong',
  AT_RISK: 'bg-warning-soft text-warning-strong',
  OVERDUE: 'bg-danger-soft text-danger-strong',
  STALE: 'bg-warning-soft text-warning-strong',
  BLOCKED: 'bg-muted text-muted-foreground',
  AWAITING_REVIEW: 'bg-info-soft text-info-strong',
};

export function bandLabel(t: T, band: WorkloadBand): string {
  switch (band) {
    case 'LOW': return t('band_low', { defaultValue: 'Low workload' });
    case 'NORMAL': return t('band_normal', { defaultValue: 'Normal workload' });
    case 'HIGH': return t('band_high', { defaultValue: 'High workload' });
    case 'VERY_HIGH': return t('band_very_high', { defaultValue: 'Very high workload' });
    default: return String(band).replace(/_/g, ' ').toLowerCase();
  }
}

export const BAND_CLASS: Record<WorkloadBand, string> = {
  LOW: 'bg-muted text-foreground',
  NORMAL: 'bg-success-soft text-success-strong',
  HIGH: 'bg-warning-soft text-warning-strong',
  VERY_HIGH: 'bg-danger-soft text-danger-strong',
};

export const BAND_BAR_CLASS: Record<WorkloadBand, string> = {
  LOW: 'bg-muted-foreground/40',
  NORMAL: 'bg-success',
  HIGH: 'bg-warning',
  VERY_HIGH: 'bg-danger',
};

export function confidenceLabel(t: T, c: WorkloadConfidence): string {
  switch (c) {
    case 'sized': return t('confidence_sized', { defaultValue: 'Based on effort sizes' });
    case 'estimated': return t('confidence_estimated', { defaultValue: 'Partly sized — some items estimated' });
    default: return t('confidence_counts_only', { defaultValue: 'Based on item counts only' });
  }
}

export function effortLabel(t: T, size: EffortSize | null | undefined): string {
  switch (size) {
    case 'XS': return t('effort_xs', { defaultValue: 'XS · Very small' });
    case 'S': return t('effort_s', { defaultValue: 'S · Small' });
    case 'M': return t('effort_m', { defaultValue: 'M · Medium' });
    case 'L': return t('effort_l', { defaultValue: 'L · Large' });
    case 'XL': return t('effort_xl', { defaultValue: 'XL · Very large' });
    default: return t('effort_none', { defaultValue: 'Not sized' });
  }
}

/** "Why?" — a popover listing the server's reasons. Never empty: when the
 * server sent no reasons we still say so, so the chip is always explainable. */
export const ReasonsPopover: React.FC<{
  reasons: string[];
  title: string;
  /** Extra line under the title (e.g. confidence). */
  subtitle?: string;
  label?: string;
  testId?: string;
}> = ({ reasons, title, subtitle, label, testId }) => {
  const { t } = useTranslation('work');
  const why = label || (t('why', { defaultValue: 'Why?' }) as string);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          aria-label={`${why} ${title}`} onClick={(e) => e.stopPropagation()}>
          <HelpCircle className="w-3.5 h-3.5" /> {why}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 text-sm" onClick={(e) => e.stopPropagation()}>
        <p className="font-medium text-charcoal">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        {reasons.length > 0 ? (
          <ul className="mt-2 space-y-1 list-disc pl-4 text-foreground">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">{t('no_reasons', { defaultValue: 'No specific reasons were reported.' })}</p>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Health chip for one work item; `explain` → "Why?" popover. */
export const HealthChip: React.FC<{
  health: WorkHealth;
  explain?: string[] | null;
  signals?: string[] | null;
  withWhy?: boolean;
  className?: string;
}> = ({ health, explain, signals, withWhy = true, className = '' }) => {
  const { t } = useTranslation('work');
  const label = healthLabel(t as unknown as T, health);
  const reasons = (explain && explain.length ? explain : signals) || [];
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={`health-chip-${health}`}>
      <Badge variant="secondary" className={HEALTH_CLASS[health] || 'bg-muted text-foreground'}>{label}</Badge>
      {withWhy && <ReasonsPopover reasons={reasons} title={label} />}
    </span>
  );
};

/** Workload band chip with reasons + confidence. */
export const BandChip: React.FC<{
  band: WorkloadBand;
  reasons?: string[] | null;
  confidence?: WorkloadConfidence;
  withWhy?: boolean;
  className?: string;
}> = ({ band, reasons, confidence, withWhy = true, className = '' }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as T;
  const label = bandLabel(tt, band);
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={`band-chip-${band}`}>
      <Badge variant="secondary" className={BAND_CLASS[band] || 'bg-muted text-foreground'}>{label}</Badge>
      {withWhy && (
        <ReasonsPopover reasons={reasons || []} title={label}
          subtitle={confidence ? confidenceLabel(tt, confidence) : undefined} testId={`band-why-${band}`} />
      )}
    </span>
  );
};

/** Effort chip (item page); nothing when not sized. */
export const EffortChip: React.FC<{ size?: EffortSize | null }> = ({ size }) => {
  const { t } = useTranslation('work');
  if (!size) return null;
  return (
    <Badge variant="outline" className="tabular-nums" data-testid="effort-chip"
      title={t('effort_help', { defaultValue: 'Relative size for workload planning — not hours.' }) as string}>
      {t('effort', { defaultValue: 'Effort' })}: {effortLabel(t as unknown as T, size)}
    </Badge>
  );
};

/** Native select for the effort size (create dialog + edit form). */
export const EffortSelect: React.FC<{
  value: EffortSize | '' | null | undefined;
  onChange: (v: EffortSize | null) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  /** Override the accessible name (default "Effort"), e.g. "Default effort". */
  ariaLabel?: string;
}> = ({ value, onChange, id = 'effort-size', className = '', disabled, ariaLabel }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as T;
  return (
    <select id={id} className={className} value={value || ''} disabled={disabled}
      aria-label={ariaLabel || (t('effort', { defaultValue: 'Effort' }) as string)}
      onChange={(e) => onChange((e.target.value || null) as EffortSize | null)}>
      <option value="">{effortLabel(tt, null)}</option>
      {(['XS', 'S', 'M', 'L', 'XL'] as EffortSize[]).map((s) => <option key={s} value={s}>{effortLabel(tt, s)}</option>)}
    </select>
  );
};
